import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Eye, 
  EyeOff, 
  Users, 
  KeyRound, 
  RefreshCw, 
  Check, 
  ArrowLeft, 
  Building2, 
  Sparkles, 
  Lock, 
  ShieldCheck, 
  Mail,
  HelpCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { supabase, fetchUserProfile } from '../lib/supabase';
import useCalendarStore from '../store/useCalendarStore';

export default function SignupPage() {
  const darkMode = useCalendarStore(s => s.darkMode);
  const navigate = useNavigate();

  // Wizard state: 'email' | 'password' | 'options' | 'join_code' | 'setup_password' | 'request_team' | 'success_setup' | 'success_request'
  const [step, setStep] = useState('email');
  
  // Inputs
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [teamName, setTeamName] = useState('');
  const [purpose, setPurpose] = useState('');
  
  // UI states
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgotTip, setShowForgotTip] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get('token');
    const emailParam = params.get('email');

    if (tokenParam) {
      setLoading(true);
      fetch(`${API_URL}/api/auth/verify-invitation?token=${encodeURIComponent(tokenParam)}`)
        .then((res) => {
          if (!res.ok) {
            return res.json().then(data => {
              throw new Error(data.detail || 'Invalid or expired invitation link');
            });
          }
          return res.json();
        })
        .then((data) => {
          setEmail(data.email);
          setStep('setup_password');
          toast.success('Invitation verified successfully!');
        })
        .catch((err) => {
          toast.error(err.message);
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (emailParam) {
      setEmail(emailParam);
      // Auto-verify pre-authorized email
      setLoading(true);
      supabase.rpc('check_user_setup', {
        email_to_check: emailParam
      })
      .then(({ data: status, error }) => {
        if (error) throw error;
        if (status === 'exists') {
          setStep('password');
        } else if (status === 'ready') {
          setStep('setup_password');
          toast.info('Account ready for onboarding!');
        } else {
          setStep('options');
        }
      })
      .catch((err) => {
        toast.error(err.message || 'Verification failed.');
      })
      .finally(() => {
        setLoading(false);
      });
    }
  }, [location.search, API_URL]);

  // 1. Submit Email
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter a valid email address');
      return;
    }
    setLoading(true);
    try {
      // Query database directly via Supabase RPC to bypass Python backend cold start
      const { data: status, error } = await supabase.rpc('check_user_setup', {
        email_to_check: email
      });
      
      if (error) throw error;
      
      if (status === 'exists') {
        // User already has an account -> Go to login password step
        setStep('password');
      } else if (status === 'ready') {
        // User is pre-authorized (admin provisioned or team approved) -> Go directly to setup password step
        toast.info('Account ready for onboarding!');
        setStep('setup_password');
      } else {
        // New email -> Go to options step
        setStep('options');
      }
    } catch (err) {
      toast.error(err.message || 'Verification failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  // 2. Login User (Scenario A)
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error('Please enter your password');
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      const profile = await fetchUserProfile(data.user);
      if (!profile) {
        await supabase.auth.signOut();
        toast.error('Account not linked to any team. Contact administration.');
        return;
      }

      // Save user profile for RBAC mapping
      localStorage.setItem('admin_profile', JSON.stringify({
        admin_id: profile.email || profile.username,
        name: profile.name,
        id: profile.id,
        team_id: profile.team_id,
        access_level: profile.access_level,
        role: profile.role,
        branch_id: profile.branch_id,
        batch_id: profile.batch_id,
        faculty_id: profile.faculty_id,
      }));

      toast.success(`Welcome back, ${profile.name || profile.email}`);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message || 'Login failed. Please verify credentials.');
    } finally {
      setLoading(false);
    }
  };

  // 3. Join Team with Join Code (Scenario B.1)
  const handleVerifyJoinCode = async (e) => {
    e.preventDefault();
    if (!joinCode) {
      toast.error('Please enter a team join code');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/join-team`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, join_code: joinCode })
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to verify join code');

      if (data.status === 'already_member') {
        toast.success(data.message);
        setStep('password');
      } else {
        toast.success(data.message);
        setStep('setup_password');
      }
    } catch (err) {
      toast.error(err.message || 'Verification failed. Please check the code.');
    } finally {
      setLoading(false);
    }
  };

  // 4. Request a new team (Scenario B.2)
  const handleRequestTeam = async (e) => {
    e.preventDefault();
    if (!teamName) {
      toast.error('University/Team name is required');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/request-team`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, team_name: teamName, purpose })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Request failed');

      setStep('success_request');
      toast.success('Team request submitted successfully!');
    } catch (err) {
      toast.error(err.message || 'Failed to submit team request');
    } finally {
      setLoading(false);
    }
  };

  // 5. Setup Password (for pre-authorized / team joined user)
  const handlePasswordSetupSubmit = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error('Password is required');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      // Create user in Supabase Auth
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { setup_flow: true }
        }
      });
      
      if (error) {
        if (error.status === 400 && error.message.includes('already registered')) {
          throw new Error('User already registered. Please login with your password.');
        }
        throw error;
      }

      if (data.user) {
        // Log out immediately to force correct auth_id synchronization and full setup cycle on login
        await supabase.auth.signOut();
        setStep('success_setup');
        toast.success('Onboarding complete!');
      } else {
        toast.success('Signup successful! Check email for confirmation.');
        setStep('email');
      }
    } catch (err) {
      toast.error(err.message || 'Password setup failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center login-bg relative overflow-hidden p-4 md:p-8" data-testid="signup-page">
      {/* Dynamic radial gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(37,99,235,0.08),transparent_45%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(6,182,212,0.08),transparent_45%)]" />

      {/* Main Container - Beautiful Horizontal split layout with padding box */}
      <div className="w-full max-w-4xl bg-white/80 dark:bg-zinc-950/80 backdrop-blur-xl rounded-3xl border border-zinc-200/50 dark:border-zinc-800/50 shadow-2xl p-6 md:p-10 relative z-10 flex flex-col md:flex-row items-center md:items-stretch gap-8 md:gap-12 transition-all duration-300">
        
        {/* Left Side: Branding - Intelligently aligned side-by-side with active form */}
        <div className="flex-1 flex flex-col items-center justify-center text-center md:pr-4">
          <div className="relative">
            {/* Elegant premium background glow */}
            <div className="absolute -inset-10 bg-gradient-to-tr from-blue-600/20 to-cyan-400/20 blur-3xl rounded-full opacity-50 pointer-events-none" />
            
            {/* Premium, interactive unified brand logo */}
            <img 
              src="/scholars_anchor_logo.png" 
              alt="Scholars Anchor Logo" 
              className="w-64 md:w-72 h-auto object-contain relative z-10 animate-float transition-all duration-300 hover:scale-[1.03] select-none"
              style={{
                filter: darkMode ? 'invert(1) hue-rotate(180deg)' : 'none'
              }}
            />
          </div>
          <p className="text-[0.65rem] md:text-xs text-zinc-400 dark:text-zinc-500 uppercase tracking-[0.35em] font-bold pl-[0.35em] mt-6 relative z-10">
            Intelligent Academic Portal
          </p>
        </div>

        {/* Divider: Clean vertical separator visible on desktop */}
        <div className="hidden md:block w-px bg-zinc-200/60 dark:bg-zinc-800/60 self-stretch my-2" />

        {/* Right Side: Active Wizard Card form */}
        <div className="flex-1 w-full max-w-md flex flex-col justify-center py-4">
          
          {/* STEP 1: ENTER EMAIL */}
          {step === 'email' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-zinc-800 dark:text-zinc-100">Welcome</h2>
                <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">Enter your email address to get started</p>
              </div>

              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email-input" className="text-xs font-bold uppercase tracking-wider text-zinc-500">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      id="email-input"
                      className="pl-10 h-12 bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 rounded-xl focus:ring-blue-500"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <p className="text-[10px] text-zinc-400 dark:text-zinc-500 leading-normal">
                    * Supports institutional as well as private email addresses (e.g. @gmail.com accounts).
                  </p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 rounded-xl text-sm font-semibold transition-all shadow-md shadow-blue-500/10 flex items-center justify-center gap-2" 
                  disabled={loading}
                >
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Continue'}
                </Button>
              </form>
            </div>
          )}

          {/* STEP 2: PASSWORD ENTRY (LOGIN) */}
          {step === 'password' && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="flex items-center justify-between">
                <button onClick={() => setStep('email')} className="flex items-center gap-1.5 text-xs font-bold text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300">
                  <ArrowLeft className="w-3.5 h-3.5" /> Back
                </button>
                <span className="text-[10px] bg-blue-50 dark:bg-blue-900/20 text-blue-600 px-2.5 py-1 rounded-full font-bold">Existing Account</span>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-zinc-800 dark:text-zinc-100">Welcome Back</h2>
                <p className="text-xs text-zinc-500 mt-1">Please enter your password for <strong className="text-zinc-700 dark:text-zinc-300">{email}</strong></p>
              </div>

              <form onSubmit={handleLoginSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      className="pl-10 pr-10 h-12 bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 rounded-xl"
                      type={showPw ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <button type="button" className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 p-1" onClick={() => setShowPw(!showPw)}>
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex flex-col items-end space-y-2">
                  <button 
                    type="button" 
                    onClick={() => setShowForgotTip(!showForgotTip)} 
                    className="text-xs text-blue-600 hover:underline font-semibold flex items-center gap-1"
                  >
                    <HelpCircle className="w-3 h-3" /> Forgot password?
                  </button>
                  
                  {showForgotTip && (
                    <div className="w-full text-left p-3 rounded-lg bg-zinc-50 dark:bg-zinc-900 border border-zinc-200/50 dark:border-zinc-800 text-[11px] text-zinc-500 leading-relaxed mt-2 animate-fadeIn">
                      ⚠️ Password recovery and resets are managed securely inside the dashboard settings panel for security. Please contact your local portal administrator to reset or re-authorize your credentials externally.
                    </div>
                  )}
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 rounded-xl text-sm font-semibold transition-all shadow-md shadow-blue-500/10" 
                  disabled={loading}
                >
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Sign In'}
                </Button>
              </form>
            </div>
          )}

          {/* STEP 3: OPTIONS SELECTION */}
          {step === 'options' && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="flex items-center justify-between">
                <button onClick={() => setStep('email')} className="flex items-center gap-1.5 text-xs font-bold text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300">
                  <ArrowLeft className="w-3.5 h-3.5" /> Back
                </button>
                <span className="text-[10px] bg-amber-50 dark:bg-amber-900/20 text-amber-600 px-2.5 py-1 rounded-full font-bold">New Account</span>
              </div>

              <div>
                <h2 className="text-xl font-bold text-zinc-800 dark:text-zinc-100">Get Access</h2>
                <p className="text-xs text-zinc-500 mt-1">Select an option to initialize your portal for <strong className="text-zinc-700 dark:text-zinc-300">{email}</strong></p>
              </div>

              <div className="grid grid-cols-1 gap-4 pt-1">
                {/* Option 1: Join Team */}
                <button
                  onClick={() => setStep('join_code')}
                  className="group flex items-start gap-4 p-4 rounded-2xl border border-zinc-200 dark:border-zinc-800 hover:border-blue-500 dark:hover:border-blue-500 bg-white/50 dark:bg-zinc-900/50 hover:bg-blue-50/20 dark:hover:bg-blue-950/10 transition-all text-left shadow-sm"
                >
                  <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 shrink-0 group-hover:scale-105 transition-transform">
                    <KeyRound className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-zinc-800 dark:text-zinc-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">Join an Existing Team</h3>
                    <p className="text-[11px] text-zinc-400 dark:text-zinc-500 mt-1 leading-normal font-medium">
                      Enter a secure random join code provided by your administrator to link with your department dashboard.
                    </p>
                  </div>
                </button>

                {/* Option 2: Request Team */}
                <button
                  onClick={() => setStep('request_team')}
                  className="group flex items-start gap-4 p-4 rounded-2xl border border-zinc-200 dark:border-zinc-800 hover:border-cyan-500 dark:hover:border-cyan-500 bg-white/50 dark:bg-zinc-900/50 hover:bg-cyan-50/20 dark:hover:bg-cyan-950/10 transition-all text-left shadow-sm"
                >
                  <div className="w-10 h-10 rounded-xl bg-cyan-100 dark:bg-cyan-900/20 flex items-center justify-center text-cyan-600 shrink-0 group-hover:scale-105 transition-transform">
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-zinc-800 dark:text-zinc-200 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">Request a New Team</h3>
                    <p className="text-[11px] text-zinc-400 dark:text-zinc-500 mt-1 leading-normal font-medium">
                      Initialize a clean, brand-new calendar and planning workspace environment for your academic institution.
                    </p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* STEP 4: ENTER JOIN CODE */}
          {step === 'join_code' && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <button onClick={() => setStep('options')} className="flex items-center gap-1.5 text-xs font-bold text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300">
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </button>

              <div>
                <h2 className="text-2xl font-bold text-zinc-800 dark:text-zinc-100">Enter Join Code</h2>
                <p className="text-xs text-zinc-500 mt-1">Please enter the secure team join code (e.g. CS-XXXXXX)</p>
              </div>

              <form onSubmit={handleVerifyJoinCode} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Team Join Code</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      className="pl-10 h-12 bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 rounded-xl font-mono uppercase tracking-widest placeholder:tracking-normal text-sm"
                      placeholder="CS-A1B2C3"
                      value={joinCode}
                      onChange={(e) => setJoinCode(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 rounded-xl text-sm font-semibold transition-all shadow-md shadow-blue-500/10" 
                  disabled={loading}
                >
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Verify & Join'}
                </Button>
              </form>
            </div>
          )}

          {/* STEP 5: SETUP PASSWORD */}
          {step === 'setup_password' && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="flex items-center justify-between">
                <button onClick={() => setStep('email')} className="flex items-center gap-1.5 text-xs font-bold text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300">
                  <ArrowLeft className="w-3.5 h-3.5" /> Restart
                </button>
                <span className="text-[10px] bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 px-2.5 py-1 rounded-full font-bold">Verification Successful</span>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-zinc-800 dark:text-zinc-100">Set Up Password</h2>
                <p className="text-xs text-zinc-500 mt-1">Create a secure password to finalize your dashboard registration.</p>
              </div>

              <form onSubmit={handlePasswordSetupSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Choose Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      className="pl-10 pr-10 h-12 bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 rounded-xl"
                      type={showPw ? 'text' : 'password'}
                      placeholder="Min. 6 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <button type="button" className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 p-1" onClick={() => setShowPw(!showPw)}>
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 rounded-xl text-sm font-semibold transition-all shadow-md shadow-blue-500/10" 
                  disabled={loading}
                >
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Finalize Account'}
                </Button>
              </form>
            </div>
          )}

          {/* STEP 6: REQUEST NEW TEAM FORM */}
          {step === 'request_team' && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <button onClick={() => setStep('options')} className="flex items-center gap-1.5 text-xs font-bold text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300">
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </button>

              <div>
                <h2 className="text-2xl font-bold text-zinc-800 dark:text-zinc-100">Request Team</h2>
                <p className="text-xs text-zinc-500 mt-1">Submit a request to build a new institution workspace</p>
              </div>

              <form onSubmit={handleRequestTeam} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">University / Institution Name</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      className="pl-10 h-12 bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 rounded-xl text-sm"
                      placeholder="e.g. Oxford Department of Engineering"
                      value={teamName}
                      onChange={(e) => setTeamName(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Purpose / Details (Optional)</Label>
                  <textarea
                    className="flex min-h-[90px] w-full rounded-xl border border-zinc-200 bg-white px-3.5 py-3 text-sm placeholder:text-zinc-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-zinc-800 dark:bg-zinc-900 dark:placeholder:text-zinc-500 resize-none"
                    placeholder="Briefly tell us how you plan to use ScholarsAnchor..."
                    value={purpose}
                    onChange={(e) => setPurpose(e.target.value)}
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 rounded-xl text-sm font-semibold transition-all shadow-md shadow-blue-500/10" 
                  disabled={loading}
                >
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Submit Request'}
                </Button>
              </form>
            </div>
          )}

          {/* STEP 7: SETUP SUCCESS CARD */}
          {step === 'success_setup' && (
            <div className="text-center py-4 space-y-4 animate-in scale-in duration-300">
              <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-950/30 flex items-center justify-center mx-auto text-emerald-600 dark:text-emerald-400 animate-scaleIn">
                <ShieldCheck className="w-8 h-8" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-zinc-800 dark:text-zinc-100">Password Configured!</h3>
                <p className="text-xs text-zinc-500 leading-relaxed max-w-sm mx-auto">
                  Your academic dashboard access has been initialized successfully. Please continue to the sign-in screen to authenticate.
                </p>
              </div>
              <Button 
                onClick={() => {
                  setStep('email');
                  navigate('/login');
                }} 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 rounded-xl text-sm font-semibold shadow-md shadow-blue-500/10 mt-2"
              >
                Go to Sign In
              </Button>
            </div>
          )}

          {/* STEP 8: REQUEST SUCCESS CARD */}
          {step === 'success_request' && (
            <div className="text-center py-4 space-y-4 animate-in scale-in duration-300">
              <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-950/30 flex items-center justify-center mx-auto text-blue-600 dark:text-blue-400 animate-scaleIn">
                <Sparkles className="w-8 h-8" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-zinc-800 dark:text-zinc-100">Request Registered</h3>
                <p className="text-xs text-zinc-500 leading-relaxed max-w-sm mx-auto">
                  Your team application has been recorded. Our administrators will review the request, and you will receive an invitation at <strong className="text-zinc-700 dark:text-zinc-300">{email}</strong> once approved.
                </p>
              </div>
              <Button 
                onClick={() => setStep('email')} 
                className="w-full bg-zinc-900 dark:bg-zinc-100 hover:bg-zinc-800 dark:hover:bg-zinc-200 text-white dark:text-zinc-900 h-12 rounded-xl text-sm font-semibold mt-2"
              >
                Reset Page
              </Button>
            </div>
          )}

          {/* Bottom branding footer */}
          <div className="text-center text-[10px] text-zinc-400/80 mt-6 border-t border-zinc-100/50 dark:border-zinc-800/50 pt-4 font-semibold">
            ScholarsAnchor Portal • Enterprise Grade Security
          </div>
        </div>

      </div>
    </div>
  );
}
